import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    applyEdgeChanges,
    applyNodeChanges,
    BaseEdge,
    EdgeLabelRenderer,
    EdgeProps,
    getSmoothStepPath,
    useReactFlow,
} from '@xyflow/react'
import './custom-edge.scss'
import { getClassNames } from '../../../../_utils/classnames'
import { Popover } from 'antd'
import { getAddNodeMutations } from '../../helpers'
import { useJourneyContext } from '../../context'
import { JourneyBuilderMode } from '../../enums'
import { LAYOUT_CONSTANTS } from '../../layout/layout-config'
import { ActionStep, AddStepType, DelayStep, JourneyEdge, JourneyNode } from '../../types/journey-nodes'
import { EdgeAction } from './edge-action'
import { InvalidJourneyComponent } from './invalid-journey-component'
import { AddStepPanel } from './add-step-panel'
import { CopyTypes } from '../../types/journey-context'
import { JourneyActionType } from '@pushly/models/lib/enums/journeys/journey-action-type'
import { RECOMMENDATION_NOTIFICATION_INTEGRATIONS } from '../../constants'
import { titleCase } from 'change-case'
import { JourneyAction } from '../../types/journey-context'
import {
    StepType,
    TriggerType,
    ECOMMERCE_ABANDONMENT_TRIGGERS,
    SAVED_ITEM_TRIGGERS,
    CATALOG_CONTEXT_TRIGGERS,
} from '@pushly/models/lib/enums/journeys'
import { safeEnumFromValue } from '@pushly/models'

export const CustomEdge = ({
    id,
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    source,
    target,
    data,
    ...props
}: EdgeProps<JourneyEdge>) => {
    // region Custom Edge Definitions

    const [state, dispatch] = useJourneyContext()
    const { getNode, getNodes, setNodes, setEdges } = useReactFlow<JourneyNode>()

    const [edgeActionOptions, setEdgeActionOptions] = useState<AddStepType[]>([StepType.DELAY])
    const [showEdgeActions, setShowEdgeActions] = useState(false)
    const [showInvalidConnection, setShowInvalidConnection] = useState(false)

    // Calculate edge path
    const [edgePath] = getSmoothStepPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
        borderRadius: 0, // Sharp 90-degree corners for orthogonal edges
    })

    // Position add button on the edge, using offset from layout constants
    const addButtonX = sourceX
    const offset = LAYOUT_CONSTANTS.ADD_BUTTON_OFFSET
    
    let addButtonY: number
    const edgeLength = Math.abs(targetY - sourceY)
    
    // For short edges, use true midpoint for visual centering
    // For longer edges, position at fixed offset from closest node
    if (edgeLength < offset * 2) {
        addButtonY = (sourceY + targetY) / 2
    } else {
        addButtonY = sourceY + offset
    }

    // endregion
    // region Interaction Handlers

    const handleAddNode = useCallback(
        (
            stepType: AddStepType,
            addConfig: { override?: ActionStep | DelayStep; overrideType?: CopyTypes; skipOpenEditor?: boolean } = {},
        ) => {
            setShowEdgeActions(false)

            const sourceNode = getNode(source)
            const targetNode = getNode(target)

            if (sourceNode && targetNode) {
                const [nodeChanges, edgeChanges, newNode] = getAddNodeMutations(
                    stepType,
                    getNodes(),
                    getNode(source)!,
                    getNode(target)!,
                    addConfig.override,
                    addConfig.overrideType,
                )

                setNodes((nodes) => applyNodeChanges(nodeChanges, nodes))
                setEdges((edges) => applyEdgeChanges(edgeChanges, edges))

                if (!addConfig.skipOpenEditor) {
                    dispatch({ action: JourneyAction.ON_NODE_ADD, data: newNode! })
                }
            }
        },
        [source, target],
    )

    // endregion

    // region onComponentMount effect
    useEffect(() => {
        const triggerType: TriggerType | undefined = safeEnumFromValue(
            TriggerType,
            state.journey?.configuration?.computed_trigger_type ?? TriggerType.SUBSCRIBED,
        )

        // Check trigger type categories
        const isEcommTrigger = ECOMMERCE_ABANDONMENT_TRIGGERS.includes(triggerType)
        const isSavedItemTrigger = SAVED_ITEM_TRIGGERS.includes(triggerType)
        const isContextAwareCatalogTrigger = CATALOG_CONTEXT_TRIGGERS.includes(triggerType)

        // Check domain capabilities
        const hasItemType = !!state.domain.ecommConfig?.itemType
        const hasRecommendationIntegration = Array.from(
            state.dependencies.integrations?.entries() ?? [],
        ).some(([_id, int]) => RECOMMENDATION_NOTIFICATION_INTEGRATIONS.includes(int.provider) && int.is_active)

        // Build available options declaratively
        const options = [
            ...edgeActionOptions,
            // always available
            JourneyActionType.SEND_NOTIFICATION,
            // when the campaign is using an ecomm trigger type
            ...(isEcommTrigger
                ? [JourneyActionType.SEND_CART_NOTIFICATION, JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION]
                : []),
            // when the domain has an active recommendation notification integration
            ...(hasRecommendationIntegration ? [JourneyActionType.SEND_RECOMMENDED_NOTIFICATION] : []),
            // when the campaign is using a saved item trigger type
            ...(isSavedItemTrigger ? [JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION] : []),
            // when the domain has an item type (available in both scheduled and triggered campaigns)
            ...(hasItemType ? [JourneyActionType.SEND_RANKED_ITEM_NOTIFICATION] : []),
        ] as AddStepType[]

        setEdgeActionOptions(options)
    }, [state.journey?.id])

    // watch for invalid connections and show UI warning when node->node incompatibility
    useEffect(() => {
        const sourceNode = getNode(source)
        const targetNode = getNode(target)
        if (sourceNode && targetNode) {
            const sourceType = sourceNode.data.step.type
            const targetType = targetNode.data.step.type

            const invalidDelayConnections = sourceType === StepType.DELAY && targetType === StepType.DELAY
            const invalidActionConnections = sourceType === StepType.ACTION && targetType === StepType.ACTION
            const invalidStepType: StepType | null = invalidDelayConnections
                ? StepType.DELAY
                : invalidActionConnections
                ? StepType.ACTION
                : null

            if (invalidStepType) {
                setShowInvalidConnection(true)
            } else {
                if (showInvalidConnection) {
                    setShowInvalidConnection(false)
                }
            }
        }
    }, [source, target])

    // endregion

    // region Watch for close popover signal
    useEffect(() => {
        if (state.closePopoversSignal > 0) {
            setShowEdgeActions(false)
        }
    }, [state.closePopoversSignal])

    // endregion

    const invalidConnectionMessage = useMemo(() => {
        const sourceType = getNodes().find((n) => n.id === source)?.data?.step?.type

        return sourceType
            ? `${titleCase(
                  sourceType,
              )} Steps cannot be adjacent. Please remove one of the ${sourceType?.toLowerCase()}s or add a step between them.`
            : 'Invalid connection. Please remove one of the steps or add a step between them.'
    }, [source])

    return (
        <>
            <BaseEdge id={id} path={edgePath} markerEnd={props.markerEnd} markerStart={props.markerStart} />
            <EdgeLabelRenderer>
                {showInvalidConnection && (
                    <div
                        className="journey-invalid-label"
                        style={{
                            transform: `translate(-225%, -50%) translate(${addButtonX}px, ${addButtonY}px)`,
                        }}
                    >
                        <InvalidJourneyComponent content={invalidConnectionMessage} />
                    </div>
                )}
                <div
                    className={getClassNames(null, 'journey-edge-custom', {
                        open: showEdgeActions,
                        interactive: state.mode !== JourneyBuilderMode.READONLY,
                    })}
                    style={{
                        transform: `translate(-50%, -50%) translate(${addButtonX}px, ${addButtonY}px)`,
                    }}
                >
                    <div
                        className={getClassNames('journey-edge-actions', {
                            open: showEdgeActions,
                        })}
                    >
                        <Popover
                            open={showEdgeActions}
                            onOpenChange={setShowEdgeActions}
                            trigger="click"
                            placement="rightTop"
                            align={{ offset: [0, -10] }}
                            destroyTooltipOnHide
                            content={
                                <AddStepPanel
                                    options={edgeActionOptions}
                                    onSelect={handleAddNode}
                                />
                            }
                        >
                            <EdgeAction visible={showEdgeActions} onClick={() => setShowEdgeActions(!showEdgeActions)} />
                        </Popover>
                    </div>
                </div>
            </EdgeLabelRenderer>
        </>
    )
}
